import { useEffect, useState } from 'react';
import parsePhoneNumber from 'libphonenumber-js';

/**
 * This hooks accepts a phone number and a phone extension;
 * it then uses a library to determine whether the phone number is valid;
 * if valid, it then uses that library to get a valid URI/href attribute.
 *
 * @param  {string} phoneNumber
 *   The phone number to validate and parse.
 * @param  {string?} phoneExtension
 *   The phone extension to attach to the phone number.
 *
 * @returns {
 *    numberWithExtension: string,
 *    isValidPhoneNumber: boolean,
 *    phoneCallHref: string,
 *    phoneSmsHref: string
 *  }
 */
const useParsePhoneNumber = (phoneNumber, phoneExtension) => {
  const [numberWithExtension, setNumberWithExtension] = useState(
    phoneExtension || String(phoneExtension) === '0' ? `${phoneNumber} ext ${phoneExtension}` : phoneNumber,
  );
  const [parsedPhoneNumber, setParsedPhoneNumber] = useState(parsePhoneNumber(numberWithExtension, 'US'));
  const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(parsedPhoneNumber?.isValid());
  const [phoneCallHref, setPhoneCallHref] = useState(
    parsedPhoneNumber ? parsedPhoneNumber.getURI() : '',
  );
  const [phoneSmsHref, setPhoneSmsHref] = useState(
    phoneCallHref ? phoneCallHref.replace('tel', 'sms') : '',
  );

  // Update the `numberWithExtension` state if props change
  useEffect(() => {
    const newNumberWithExtension = phoneExtension || String(phoneExtension) === '0'
      ? `${phoneNumber} ext ${phoneExtension}`
      : phoneNumber;
    if (newNumberWithExtension !== numberWithExtension) {
      setNumberWithExtension(newNumberWithExtension);
    }
  }, [phoneNumber, phoneExtension, numberWithExtension]);

  // Update the `parsedPhoneNumber` state if `numberWithExtension` changes
  useEffect(() => {
    const newParsed = parsePhoneNumber(numberWithExtension, 'US');
    setParsedPhoneNumber(newParsed);
  }, [numberWithExtension]);

  // Update the `isValidPhoneNumber` state if `parsedPhoneNumber` changes
  useEffect(() => {
    const isValid = parsedPhoneNumber?.isValid();
    if (isValid !== isValidPhoneNumber) {
      setIsValidPhoneNumber(isValid);
    }
  }, [parsedPhoneNumber, isValidPhoneNumber]);

  // Update the `phoneCallHref` state if `parsedPhoneNumber` changes
  useEffect(() => {
    const newHref = parsedPhoneNumber ? parsedPhoneNumber.getURI() : '';
    if (newHref !== phoneCallHref) {
      setPhoneCallHref(newHref);
    }
  }, [parsedPhoneNumber, phoneCallHref]);

  // Update the `phoneSmsHref` state if `phoneCallHref` changes
  useEffect(() => {
    const newHref = phoneCallHref ? phoneCallHref.replace('tel', 'sms') : '';
    if (newHref !== phoneSmsHref) {
      setPhoneSmsHref(newHref);
    }
  }, [phoneSmsHref, phoneCallHref]);

  return {
    numberWithExtension,
    isValidPhoneNumber,
    phoneCallHref,
    phoneSmsHref,
  };
};

export default useParsePhoneNumber;
